import React from "react";
import {
    DiferenciaOpcional,
    RenderKgSecos,
    RenderPorcentaje,
} from "../../components/CeldasReporte";

// la producción obtenida es la misma en las tres filas: lo que cambia entre
// ellas es de dónde se tomó lo enviado a producción
const tituloColumnaComun =
    "Valor común en las tres filas, calculado a partir del consumo real de las tancadas";

// cada método toma el reproceso de la misma fuente que su materia prima
const tituloColumnaReproceso =
    "En los métodos 1 y 2 proviene de los cierres de inventario de almacén; en el método 3, del consumo real de las tancadas";

const METODO_POR_ORIGEN = {
    compra: "Método 1",
    liquidacion: "Método 2",
    tolva: "Método 3",
};

// Qué faltante de los traslados deja incompleta la diferencia de cada fila, en
// singular y en plural. Se cuentan movimientos y al usuario se le nombran como
// recepciones, igual que en los motivos del cierre. La fila de tolva no tiene
// diferencia acopio, por eso no aparece
const RECEPCION_ACOPIO_SIN_ANALISIS = [
    "acopio_sin_analisis",
    "recepción de acopio sin análisis de laboratorio validado",
    "recepciones de acopio sin análisis de laboratorio validado",
];

const RECEPCION_ACOPIO_SIN_LIQUIDAR = [
    "acopio_sin_liquidar",
    "recepción de acopio sin liquidar",
    "recepciones de acopio sin liquidar",
];

const RECEPCION_PLANTA_SIN_ANALISIS = [
    "planta_sin_analisis",
    "recepción por traslado en planta sin análisis de laboratorio validado",
    "recepciones por traslado en planta sin análisis de laboratorio validado",
];

const FALTANTES_POR_ORIGEN = {
    compra: [RECEPCION_ACOPIO_SIN_ANALISIS, RECEPCION_PLANTA_SIN_ANALISIS],
    liquidacion: [RECEPCION_ACOPIO_SIN_LIQUIDAR, RECEPCION_PLANTA_SIN_ANALISIS],
};

// Los kg de los traslados siguen entrando después de la fecha de corte, así que
// una diferencia calculada con recepciones incompletas todavía va a cambiar
const tituloIncompleto = (origen, pendientes) => {
    if (!pendientes) return "";

    const faltantes = (FALTANTES_POR_ORIGEN[origen] || [])
        .filter(([clave]) => pendientes[clave])
        .map(
            ([clave, singular, plural]) =>
                `${pendientes[clave]} ${
                    pendientes[clave] === 1 ? singular : plural
                }`,
        );

    if (!faltantes.length) return "";

    return "Diferencia preliminar: existen " + faltantes.join(" y ") + ".";
};

// La pantalla ya resolvió antes que haya datos que mostrar, por eso aquí solo
// se arma la tabla
export const ComparativoMensualTable = ({
    comparativo,
    pendientesTraslados,
}) => {
    return (
        <div className="new-table-wrapper my-4 sticky-header sticky-first-column sticky-second-column comparativo-mensual__table-wrapper">
            <table className="table-striped-v2">
                <thead>
                    <tr>
                        <th className="align-middle">Método</th>
                        <th className="align-middle cell-border-right comparativo-mensual__descripcion">
                            Descripción del método
                        </th>

                        <th className="align-middle">Materia prima</th>
                        <th
                            className="align-middle text-nowrap"
                            title={tituloColumnaReproceso}
                        >
                            (+) reproceso
                        </th>
                        <th className="align-middle">
                            Total enviado a producción
                        </th>

                        <th
                            className="align-middle cell-border-left"
                            title={tituloColumnaComun}
                        >
                            Total producción sólidos
                        </th>
                        <th
                            className="align-middle text-nowrap"
                            title={tituloColumnaComun}
                        >
                            Miga (pérdida neta)
                        </th>
                        <th className="align-middle" title={tituloColumnaComun}>
                            Total producción
                        </th>

                        <th className="align-middle cell-border-left">
                            Diferencia en kg
                        </th>
                        <th className="align-middle text-nowrap">
                            Diferencia en %
                        </th>

                        <th className="align-middle text-nowrap cell-border-left">
                            Diferencia acopio
                        </th>
                        <th className="align-middle text-nowrap">
                            Diferencia total
                        </th>
                        <th className="align-middle text-nowrap">
                            % Diferencia total
                        </th>
                    </tr>
                </thead>

                <tbody>
                    {comparativo.filas.map((fila) => {
                        const incompleta = tituloIncompleto(
                            fila.origen_chipa,
                            pendientesTraslados,
                        );

                        const claseIncompleta = incompleta
                            ? " comparativo-mensual__celda-incompleta"
                            : "";

                        return (
                            <tr key={fila.origen_chipa}>
                                <td className="text-center fw-bold">
                                    {METODO_POR_ORIGEN[fila.origen_chipa]}
                                </td>
                                <td className="fw-bold cell-border-right">
                                    {fila.descripcion}
                                </td>

                                <td className="text-right text-nowrap">
                                    <RenderKgSecos value={fila.materia_prima} />
                                </td>
                                <td className="text-right text-nowrap">
                                    <RenderKgSecos value={fila.reproceso} />
                                </td>
                                <td className="text-right text-nowrap">
                                    <RenderKgSecos value={fila.total_enviado} />
                                </td>

                                <td className="text-right text-nowrap cell-border-left">
                                    <RenderKgSecos
                                        value={fila.produccion_solidos}
                                    />
                                </td>
                                <td className="text-right text-nowrap">
                                    <RenderKgSecos value={fila.miga} />
                                </td>
                                <td className="text-right text-nowrap">
                                    <RenderKgSecos
                                        value={fila.total_produccion}
                                    />
                                </td>

                                <td className="text-right text-nowrap cell-border-left">
                                    <RenderKgSecos value={fila.diferencia_kg} />
                                </td>
                                <td className="text-right text-nowrap">
                                    <RenderPorcentaje
                                        value={fila.diferencia_porcentaje}
                                    />
                                </td>

                                <td
                                    className={`text-right text-nowrap cell-border-left${claseIncompleta}`}
                                    title={incompleta}
                                >
                                    <DiferenciaOpcional
                                        fila={fila}
                                        campo="diferencia_acopio"
                                    >
                                        <RenderKgSecos
                                            value={fila.diferencia_acopio}
                                        />
                                    </DiferenciaOpcional>
                                </td>
                                <td
                                    className={`text-right text-nowrap ${claseIncompleta}`}
                                    title={incompleta}
                                >
                                    <DiferenciaOpcional
                                        fila={fila}
                                        campo="diferencia_total"
                                    >
                                        <RenderKgSecos
                                            value={fila.diferencia_total}
                                        />
                                    </DiferenciaOpcional>
                                </td>
                                <td
                                    className={`text-right text-nowrap ${claseIncompleta}`}
                                    title={incompleta}
                                >
                                    <DiferenciaOpcional
                                        fila={fila}
                                        campo="diferencia_total_porcentaje"
                                    >
                                        <RenderPorcentaje
                                            value={
                                                fila.diferencia_total_porcentaje
                                            }
                                        />
                                    </DiferenciaOpcional>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};
